<template>
  <Form
    :validation-schema="schema"
    :initial-values="props.initialValues"
    @submit="handleForm"
  >
    <div
      class="py-4 px-4 sm:px-8 bg-[#F9FAFC] w-full mt-8 flex flex-col gap-y-8 pt-8 rounded-md"
    >
      <!-- Sezione: Personal Information -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Personal Information</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <div class="grid gap-4 mt-2 md:grid-cols-2">
          <!-- Full name -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="fullName">Full name</label>
            <Field
              name="fullName"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert your full name..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
              />
            </Field>
            <ErrorMessage name="fullName" class="text-sm text-red-500" />
          </div>
          <!-- Email -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="email">Email</label>
            <Field
              name="email"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors, handleChange }"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert an email..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
                @blur="trimEmailField(field.value, handleChange)"
              />
            </Field>
            <ErrorMessage name="email" class="text-sm text-red-500" />
          </div>
          <!-- Contact number -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="contactNumber">Contact number</label>
            <IntlTelInput
              class="w-full"
              :class="{ 'ring-red-500 border-red-500': hasBeenSubmitted && !isPhoneValid }"
              @changeNumber="phoneNumber = $event"
              @changeValidity="isPhoneValid = $event" 
              @changeErrorCode="phoneErrorCode = $event"
              :options="{
                initialCountry: 'us',
              }"
            />
            <span v-if="hasBeenSubmitted && !isPhoneValid" class="text-sm text-red-500">
              {{ getPhoneErrorMessage() }}
            </span>
          </div>
          <!-- Location -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="location">Location</label>
            <Field
              name="location"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <div class="relative">
                <select
                  v-bind="field"
                  class="w-full appearance-none pr-8"
                  :class="{ 'ring-red-500 border-red-500': errors[0] }"
                  :disabled="loading"
                >
                  <option value="">Select a country...</option>
                  <option
                    v-for="country in countries"
                    :key="country"
                    :value="country"
                  >
                    {{ country }}
                  </option>
                </select>
                <div
                  class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                    class="w-4 h-4"
                  >
                    <path
                      d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                    />
                  </svg>
                </div>
              </div>
            </Field>
            <ErrorMessage name="location" class="text-sm text-red-500" />
          </div>
        </div>
      </div>

      <!-- Sezione: Professional Background -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Professional Background</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>
        <div class="grid gap-4 mt-2">
          <!-- Years of experience -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]" for="yearsExperience">
              Years of experience
            </label>
            <Field
              name="yearsExperience"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <input
                v-bind="field"
                type="number"
                placeholder="Insert years of experience..."
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                style="appearance: textfield"
                :disabled="loading"
                @keypress="
                  (e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }
                "
                @paste="
                  (e) => {
                    e.preventDefault();
                    const text = e.clipboardData.getData('text');
                    if (/^[0-9]+$/.test(text)) {
                      e.target.value = text;
                    }
                  }
                "
              />
            </Field>
            <ErrorMessage name="yearsExperience" class="text-sm text-red-500" />
          </div>

          <!-- Genres -->
          <FieldArray name="gernes" v-slot="{ fields: gernes, push, remove }">
            <div class="flex flex-col gap-y-4">
              <!-- First genre field - always visible -->
              <div class="flex flex-col gap-y-1">
                <label class="font-medium text-[#7B7979]">{{ labelGerne[0] }}</label>
                <div class="flex flex-row gap-2">
                  <Field
                    name="gernes[0]"
                    :validateOnInput="hasBeenSubmitted"
                    v-slot="{ field: fieldProps, errors }"
                  >
                    <div class="relative grow">
                      <select
                        v-bind="fieldProps"
                        class="w-full appearance-none pr-8"
                        :class="{ 'ring-red-500 border-red-500': errors[0] }"
                        :disabled="loading"
                      >
                        <option value="">Select genre</option>
                        <option
                          v-for="genre in availableGenres(gernes, 0)"
                          :key="genre"
                          :value="genre"
                        >
                          {{ genre }}
                        </option>
                      </select>
                      <div
                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 320 512"
                          class="w-4 h-4"
                        >
                          <path
                            d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                          />
                        </svg>
                      </div>
                    </div>
                  </Field>
                </div>
                <ErrorMessage name="gernes[0]" class="text-sm text-red-500" />
              </div>

              <!-- Additional genre fields -->
              <div
                v-for="(field, index) in gernes.slice(1)"
                :key="field.key"
                class="flex flex-col gap-y-1"
              >
                <label class="font-medium text-[#7B7979]">{{
                  labelGerne[index + 1]
                }}</label>
                <div class="flex flex-row gap-2">
                  <Field
                    :name="`gernes[${index + 1}]`"
                    :validateOnInput="hasBeenSubmitted"
                    v-slot="{ field: fieldProps, errors }"
                  >
                    <div class="relative grow">
                      <select
                        v-bind="fieldProps"
                        class="w-full appearance-none pr-8"
                        :class="{ 'ring-red-500 border-red-500': errors[0] }"
                        :disabled="loading"
                      >
                        <option value="">Select genre</option>
                        <option
                          v-for="genre in availableGenres(gernes, index + 1)"
                          :key="genre"
                          :value="genre"
                        >
                          {{ genre }}
                        </option>
                      </select>
                      <div
                        class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 320 512"
                          class="w-4 h-4"
                        >
                          <path
                            d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                          />
                        </svg>
                      </div>
                    </div>
                  </Field>
                  <button
                    class="btn-form"
                    type="button"
                    @click="() => remove(index + 1)"
                    :disabled="loading"
                  >
                    -
                  </button>
                </div>
                <ErrorMessage
                  :name="`gernes[${index + 1}]`"
                  class="text-sm text-red-500"
                />
              </div>

              <!-- Add new genre button -->
              <button
                v-if="gernes.length < 3"
                type="button"
                class="w-full btn-form self-start py-2"
                @click="() => push('')"
                :disabled="gernes.length >= 3 || loading"
              >
                + Add an additional genre
              </button>
            </div>
          </FieldArray>

          <!-- Daw Used -->
          <div class="flex flex-col gap-y-1">
            <label class="font-medium text-[#7B7979]">Daw used</label>
            <Field
              name="dawsUsed"
              :validateOnInput="hasBeenSubmitted"
              v-slot="{ field, errors }"
            >
              <input
                v-bind="field"
                type="text"
                placeholder="Insert your DAW (e.g., FL Studio, Ableton...)"
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
              />
            </Field>
            <ErrorMessage name="dawsUsed" class="text-sm text-red-500" />
          </div>

          <!-- Portfolio Links -->
          <div class="flex flex-col w-full gap-y-1">
            <label class="font-medium text-[#7B7979]">Portfolio Links</label>
            <div class="flex flex-col gap-y-5">
              <!-- Channel fields -->
              <FieldArray name="additionalChannels" v-slot="{ fields, push, remove }">
                <!-- Ensure at least one field is always visible -->
                <div class="flex flex-col gap-y-4">
                  <!-- First default field - always visible -->
                  <div class="flex gap-x-2">
                    <div class="grow">
                      <div class="flex-col md:flex-row flex gap-x-2 gap-y-1">
                        <Field
                          name="additionalChannels[0].type"
                          :validateOnInput="hasBeenSubmitted"
                          v-slot="{ field: fieldProps, errors }"
                        >
                          <div class="relative w-full md:max-w-[15rem]">
                            <select
                              v-bind="fieldProps"
                              class="w-full appearance-none text-ellipsis whitespace-nowrap overflow-hidden"
                              style="padding-right: 2rem;"
                              :class="{ 'ring-red-500 border-red-500': errors[0] }"
                              :disabled="loading"
                            >
                              <option value="">Select channel type</option>
                              <option
                                v-for="channel in availableChannels(fields, 0)"
                                :key="channel.value"
                                :value="channel.value"
                              >
                                {{ channel.label }}
                              </option>
                            </select>
                            <div
                              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                class="w-4 h-4"
                              >
                                <path
                                  d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                                />
                              </svg>
                            </div>
                          </div>
                        </Field>
                        <div class="flex gap-x-2 w-full justify-between">
                          <Field
                            name="additionalChannels[0].url"
                            :validateOnInput="hasBeenSubmitted"
                            v-slot="{ field: fieldProps, errors }"
                          >
                            <input
                              v-bind="fieldProps"
                              type="text"
                              placeholder="Insert channel URL"
                              class="grow"
                              :class="{ 'ring-red-500 border-red-500': errors[0] }"
                              :disabled="loading"
                            />
                          </Field>
                        </div>
                      </div>
                      <ErrorMessage
                        name="additionalChannels[0].url"
                        class="text-sm text-red-500"
                      />
                    </div>
                  </div>

                  <!-- Additional fields -->
                  <div
                    v-for="(field, index) in fields.slice(1)"
                    :key="field.key"
                    class="flex gap-x-2"
                  >
                    <div class="grow">
                      <div class="flex-col md:flex-row flex gap-x-2 gap-y-1">
                        <Field
                          :name="`additionalChannels[${index + 1}].type`"
                          :validateOnInput="hasBeenSubmitted"
                          v-slot="{ field: fieldProps, errors }"
                        >
                          <div class="relative w-full md:max-w-[15rem]">
                            <select
                              v-bind="fieldProps"
                              class="w-full appearance-none pr-8"
                              :class="{ 'ring-red-500 border-red-500': errors[0] }"
                              :disabled="loading"
                            >
                              <option value="">Select channel type</option>
                              <option
                                v-for="channel in availableChannels(fields, index + 1)"
                                :key="channel.value"
                                :value="channel.value"
                              >
                                {{ channel.label }}
                              </option>
                            </select>
                            <div
                              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 320 512"
                                class="w-4 h-4"
                              >
                                <path
                                  d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                                />
                              </svg>
                            </div>
                          </div>
                        </Field>
                        <div class="flex gap-x-2 w-full justify-between">
                          <Field
                            :name="`additionalChannels[${index + 1}].url`"
                            :validateOnInput="hasBeenSubmitted"
                            v-slot="{ field: fieldProps, errors }"
                          >
                            <input
                              v-bind="fieldProps"
                              type="text"
                              placeholder="Insert channel URL"
                              class="grow"
                              :class="{ 'ring-red-500 border-red-500': errors[0] }"
                              :disabled="loading"
                            />
                          </Field>
                          <button
                            type="button"
                            class="btn-form"
                            @click="() => remove(index + 1)"
                            :disabled="loading"
                          >
                            -
                          </button>
                        </div>
                      </div>
                      <ErrorMessage
                        :name="`additionalChannels[${index + 1}].url`"
                        class="text-sm text-red-500"
                      />
                    </div>
                  </div>

                  <!-- Add new channel button -->
                  <button
                    v-if="fields.length < availableChannelTypes.length"
                    type="button"
                    class="w-full btn-form self-start py-2"
                    @click="() => push({ type: '', url: '' })"
                    :disabled="fields.length >= availableChannelTypes.length || loading"
                  >
                    + Add an addition channel field
                  </button>
                </div>
              </FieldArray>
            </div>
          </div>
        </div>
      </div>

      <!-- Sezione: Technical Setup Confirmation -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Technical Setup Confirmation</h4>
        <div class="w-full h-[1px] bg-gray-200"></div>

        <div
          class="flex items-start gap-x-0 mt-4 flex-col sm:items-center gap-y-2 sm:flex-row sm:gap-y-2 sm:gap-x-2"
        >
          <div class="sm:contents flex items-start w-full">
            <Field
              name="internetConnection"
              type="checkbox"
              class="h-5 w-full max-w-[20px] cursor-pointer transition-all appearance-none rounded-md border border-slate-300 checked:bg-principal-500 checked:border-principal-500"
              id="internetConnection"
              value="true"
              :disabled="loading"
            >
            </Field>
            <label
              for="internetConnection"
              class="text-sm text-gray-900 cursor-pointer ms-1"
            >
              I have reliable internet connection
            </label>
          </div>
          <ErrorMessage name="internetConnection" class="text-sm text-red-500" />
        </div>

        <div
          class="flex items-start gap-x-0 mt-4 flex-col sm:items-center gap-y-2 sm:flex-row sm:gap-y-2 sm:gap-x-2"
        >
          <div class="sm:contents flex items-start w-full">
            <Field
              name="hardwareSoftwareSetup"
              type="checkbox"
              class="h-5 w-full max-w-[20px] cursor-pointer transition-all appearance-none rounded-md border border-slate-300 checked:bg-principal-500 checked:border-principal-500"
              id="hardwareSoftwareSetup"
              value="true"
              :disabled="loading"
            >
            </Field>
            <label
              for="hardwareSoftwareSetup"
              class="text-sm text-gray-900 cursor-pointer ms-1"
            >
              I have the necessary hardware/software setup
            </label>
          </div>
          <ErrorMessage name="hardwareSoftwareSetup" class="text-sm text-red-500" />
        </div>
      </div>

      <!-- Sezione: Availability -->
      <div>
        <h4 class="text-xl font-medium md:text-2xl">Availability</h4>
        <div class="w-full h-[1px] bg-gray-200 mb-2"></div>
        <label class="font-medium text-[#7B7979]" for="availability"
          >Expected Weekly Hours</label
        >
        <div class="relative flex flex-col w-full mt-1 gap-y-1">
          <Field
            name="availability"
            :validateOnInput="hasBeenSubmitted"
            v-slot="{ field, errors }"
          >
            <div class="relative w-full">
              <select
                v-bind="field"
                class="w-full appearance-none pr-8"
                :class="{ 'ring-red-500 border-red-500': errors[0] }"
                :disabled="loading"
              >
                <option value="">Select availability</option>
                <option value="10 hours">10 hours</option>
                <option value="10-20 hours">10-20 hours</option>
                <option value="20-30 hours">20-30 hours</option>
                <option value="30-40 hours">30-40 hours</option>
                <option value="40+ hours">40+ hours</option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                  class="w-4 h-4"
                >
                  <path
                    d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
                  />
                </svg>
              </div>
            </div>
          </Field>
          <ErrorMessage name="availability" class="text-sm text-red-500" />
        </div>
      </div>
    </div>
    <div class="w-full flex justify-end py-8 px-2 sm:px-0">
      <ButtonComponent
        @click="hasBeenSubmitted = true"
        type="submit"
        :class="['btn-primary', 'flex', 'items-center', 'gap-y-2']"
        text="Next"
        :loading="loading"
        :disabled="loading"
      >
        Next
      </ButtonComponent>
    </div>
  </Form>

  <CodeOTP
    v-if="viewCodeOTP"
    :email="emailEntered"
    @next="onSubmit"
    @back="deleteSectionCodeOTP"
  />
  <MessageComponent />
</template>

<script setup>
import CodeOTP from "@/shared/component/CodeOTP.vue";
import { Field, Form, ErrorMessage, FieldArray } from "vee-validate";
import { defineProps, defineEmits, onMounted, ref } from "vue";
import * as yup from "yup";
import axios from "axios";
import MessageComponent from "@/shared/component/MessageComponent.vue";
import { useMessage } from "@/shared/services/messageServices";
import ButtonComponent from "@/shared/component/ButtonComponent.vue";
import { countries } from "@/shared/utils/countries.js";
import { trimEmailField } from "@/shared/utils/helper.js";
import IntlTelInput from "intl-tel-input/vueWithUtils";
import "intl-tel-input/styles";

const messagesService = useMessage();

const hasBeenSubmitted = ref(false);
const viewCodeOTP = ref(false);

const props = defineProps({
  initialValues: Object,
});

const loading = ref(false);

const emailEntered = ref("");
const valuesForm = ref({});

const emit = defineEmits(["next"]);

/* const urlRegex = /^(https?:\/\/)([\w\\-]+\.)+([a-zA-Z]{2,})(\/[\w\-.,@?^=%&:\\/~+#]*)?(\?[^\s]*)?$/i; */
const urlRegex = /^(https?:\/\/)?([\w-]+(\.[\w-]+)*\.[a-z]{2,})(\/[\w@:%_+.~#?&//=()-]*)?$/i;

const schema = yup.object().shape({
  fullName: yup
    .string()
    .required("Full name is required")
    .min(2, "Name must be at least 2 characters"),
  email: yup.string().required("Email is required").email("Invalid email format"),
  location: yup.string().required("Location is required"),
  yearsExperience: yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required("Years of experience is required")
    .min(0, "Years must be positive")
    .typeError("Must be a number"),
  dawsUsed: yup
    .string()
    .min(2, "Name must be at least 2 characters")
    .required("Daw Used is required"),
  gernes: yup.array().of(yup.string().required("Genre is required").label("Genre")),
  additionalChannels: yup.array().of(
    yup.object().shape({
      type: yup.string(),
      url: yup.string().test({
        name: "is-valid-url-when-present",
        message: "Must be a valid URL",
        test: function (value) {
          // If empty, return true (valid)
          if (!value || value.trim() === "") return true;
          // Otherwise validate URL format
          return urlRegex.test(value);
        },
      }),
    })
  ),
  internetConnection: yup
    .boolean()
    .required("This field is required")
    .oneOf([true], "This field is required"),
  hardwareSoftwareSetup: yup
    .boolean()
    .required("This field is required")
    .oneOf([true], "This field is required"),
  availability: yup.string().required("Availability is required"),
});

// Phone validation
const phoneNumber = ref("");
const isPhoneValid = ref(false);
const phoneErrorCode = ref(null);

function getPhoneErrorMessage() {
  if (!phoneNumber.value) return "Contact number is required";
  if (!isPhoneValid.value) return "Please enter a valid phone number";
  return "";
}



const labelGerne = ["Primary Genre", "Second Genre", "Third Genre"];

const allGenres = [
  "Pop",
  "Hip Hop / Rap",
  "R&B",
  "Rock",
  "Electronic / EDM",
  "Reggaeton",
  "Salsa",
  "Bachata",
  "Merengue",
  "Cumbia",
  "K-pop",
  "J-pop",
  "Reggae",
  "Metal",
  "Punk",
  "Country",
  "Soul",
  "Funk",
  "Disco",
];

const availableChannelTypes = [
  { value: "youtubeChannel", label: "YouTube Channel" },
  { value: "beatstarsChannel", label: "Beatstars Channel" },
  { value: "soundcloudChannel", label: "SoundCloud Channel" },
  { value: "personalWebsite", label: "Personal Website" },
  { value: "cloudStorage", label: "Cloud Storage (Google Drive, Dropbox, etc.)" },
  {
    value: "dspAccounts",
    label: "DSP Accounts (Spotify, Apple Music, Prime Music)",
  },
];

function availableGenres(genres, currentIndex) {
  const selectedGenres = genres.map((genre, idx) =>
    idx !== currentIndex ? genre.value : null
  );
  return allGenres.filter((genre) => !selectedGenres.includes(genre));
}

function availableChannels(fields, currentIndex) {
  // Get all selected channel types except the current one
  const selectedTypes = fields
    .map((field, idx) => (idx !== currentIndex ? field.value?.type : null))
    .filter((type) => type !== null);

  // Return only channel types that haven't been selected yet
  return availableChannelTypes.filter(
    (channel) => !selectedTypes.includes(channel.value)
  );
}

async function handleForm(values) {
  // Check phone validity
  if (!phoneNumber.value || !isPhoneValid.value) {
    hasBeenSubmitted.value = true; // Trigger validation display
    return;
  }
  
  emailEntered.value = values.email;
  const transformedValues = { ...values };

  // Add phone number from IntlTelInput
  transformedValues.contactNumber = phoneNumber.value;
  
  const isEmailPresent = await verifyEmail(values.email);
  if (!isEmailPresent) {
    return;
  }

  if (transformedValues.additionalChannels?.length > 0) {
    transformedValues.additionalChannels.forEach((channel) => {
      if (channel.type && channel.url) {
        transformedValues[channel.type] = channel.url;
      }
    });

    delete transformedValues.additionalChannels;
  }

  delete valuesForm.value.email;
  valuesForm.value = transformedValues;
  viewSectionCodeOTP(transformedValues);
}

async function verifyEmail(email) {
  loading.value = true;
  if (props.initialValues.email != emailEntered.value) {
    try {
      await axios.post(`${process.env.VUE_APP_ENDPOINT_API}/work-with-us/verify-form`, {
        email: email,
        role: props.initialValues.role,
      });
      return true;
    } catch (error) {
      messagesService.error(
        "An email is already in our platform. Please try with another email"
      );
      return false;
    } finally {
      loading.value = false;
    }
  } else return true;
}

function viewSectionCodeOTP() {
  if (props.initialValues.email != emailEntered.value) viewCodeOTP.value = true;
  else emit("next", valuesForm.value);
}

function deleteSectionCodeOTP() {
  viewCodeOTP.value = false;
}

function onSubmit() {
  valuesForm.value.email = emailEntered.value;
  emit("next", valuesForm.value);
}

onMounted(() => {
  window.scrollTo({ top: 1, behavior: "smooth" });
});
</script>
